import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import trans, { isRTL } from "../../utils";
import AntdTable from "../antd/Table/Table";
import { popupcontext } from "../../context/Popupscontext";
import { useGetAllCompaniesQuery } from "../../Redux/service/Companies/Company";
import { filtrationcontext } from "../../context/Filtercontext";
import { formatDateString } from "../../JsHelpers/DateFormat";
import CompaniesHead from "./CompaniesHead";
import Actions from "../antd/Table/Actions";
import { Tag } from "antd";
import BreadCrumb from "../Header/SecondHeader";
import FormCompany from "./FormCompany/FormCompany";
import usePermission from "../../hooks/usePermission";
import AccessDenied from "../AccessDenied";
import AssignProjectsPopup from "./AssignProjectsPopup";

const Companies = () => {
	const [companyForm, setCompanyForm] = useState(false);
	const [refresh, setRefresh] = useState(false);
	const {
		handleShow,
		deleteItem,
		setDeleteItem,
		setEdited,
		edited,
		currentCompanyId,
		isEditing,
		handleClose,
	} = useContext(popupcontext);
	const [currentPage, setCurrentPage] = useState(1);
	const [filter, setFilter] = useState({
		status: null,
		trade: null,
	});
	const {
		create,
		view,
		edit: canEdit,
		delete: canDelete,
	} = usePermission("SETUPCOMPANIES");

	const buildQueryString = (filterObj) => {
		const query = Object.entries(filterObj)
			.filter(
				([_, value]) => value !== null && value !== "" && value !== undefined,
			)
			.map(([key, value]) => `${key}=${value}`)
			.join("&");

		return query ? `?${query}` : "";
	};

	const statusColors = {
		ACTIVE: `green`,
		INACTIVE: `red`,
	};

	const queryString = buildQueryString(filter);
	const {
		limit,
		setLimit,
		setSort: SetSortFilter,
		searchQuery,
		setSearchQuery,
	} = useContext(filtrationcontext);
	const [sort, setSort] = useState({
		by: "",
		order: "",
	});
	const {
		data: CompaniesData,
		isFetching,
		refetch,
	} = useGetAllCompaniesQuery(
		{
			page: currentPage,
			limit: limit,
			search: searchQuery,
			sortby: sort.by,
			order: sort.order,
			queryString: queryString,
		},
		{
			skip: !currentPage || !limit,
			refetchOnMountOrArgChange: true,
		},
	);

	useEffect(() => {
		return () => {
			SetSortFilter({ type: "", value: "" });
			setSearchQuery("");
		};
	}, [SetSortFilter, setSearchQuery]);

	useEffect(() => {
		async function fetchdata() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
			if (refresh) {
				await refetch();
				setRefresh(false);
			}
		}
		fetchdata();
	}, [edited, refresh, refetch, setEdited]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery]);

	useEffect(() => {
		localStorage.setItem("page", currentPage);
	}, [currentPage]);

	// Handle company editing from popupcontext
	useEffect(() => {
		if (isEditing && currentCompanyId) {
			setCompanyForm(currentCompanyId);
		} else if (
			!isEditing &&
			currentCompanyId === null &&
			typeof companyForm === "number"
		) {
			// Reset form when editing is closed
			setCompanyForm(false);
		}
	}, [isEditing, currentCompanyId]);

	const handleTableChange = (pagination, filters, sorter) => {
		if (sorter.order) {
			setSort({
				by: sorter.field,
				order: sorter.order === "ascend" ? "asc" : "desc",
			});
		} else {
			setSort({
				by: "",
				order: "",
			});
		}
	};

	useEffect(() => {
		if (deleteItem.condition === true && deleteItem.value === "company") {
			refetch();
			if (CompaniesData?.data.length === 1 && currentPage > 1) {
				setCurrentPage(currentPage - 1);
				localStorage.setItem("page", currentPage);
			}
			setDeleteItem({ condition: null, value: null });
		}
	}, [
		currentPage,
		deleteItem.condition,
		deleteItem.value,
		refetch,
		setDeleteItem,
		CompaniesData?.data,
	]);

	const handleAssignProjects = (companyId) => {
		handleShow("assignProjects", "company", companyId);
	};

	const columns = [
		{
			title: trans("Company Code"),
			dataIndex: "code",
			key: "code",
			width: 150,
			sorter: true,
		},
		{
			title: trans("Company Name"),
			dataIndex: "name",
			key: "name",
			width: 200,
			sorter: true,
			ellipsis: true,
		},
		// {
		//   title: trans("Trade"),
		//   dataIndex: "trade",
		//   key: "trade",
		//   width: 200,
		//   sorter: true,
		//   render: (trade) => {
		//     if (Array.isArray(trade)) {
		//       return trade.join(", ");
		//     }
		//     return trade || "-";
		//   },
		// },
		{
			title: trans("License Number"),
			dataIndex: "licenseNumber",
			key: "licenseNumber",
			width: 150,
			sorter: true,
			render: (licenseNumber) => licenseNumber || "-",
		},
		{
			title: trans("Contact Person"),
			dataIndex: "contactPerson",
			key: "contactPerson",
			width: 150,
			sorter: true,
			render: (contactPerson) => contactPerson || "-",
		},
		{
			title: trans("Contact Email"),
			dataIndex: "contactEmail",
			key: "contactEmail",
			width: 200,
			sorter: true,
			render: (contactEmail) => contactEmail || "-",
		},
		{
			title: trans("Status"),
			dataIndex: "status",
			key: "status",
			width: 120,
			sorter: true,
			render: (status) => (
				<Tag
					color={statusColors[status] || statusColors.INACTIVE}
					className="text-capitalize"
				>
					{trans(status)}
				</Tag>
			),
		},
		{
			title: trans("Projects Count"),
			dataIndex: "_count",
			key: "_count",
			width: 120,
			render: (count) => count?.companyProjects || 0,
		},
		{
			title: trans("Actions"),
			width: 150,
			fixed: !isRTL ? "right" : undefined,
			render: (_, record) => (
				<Actions
					companyId={record.id}
					type="company"
					setEditCompany={setCompanyForm}
					canEdit={canEdit}
					canDelete={canDelete}
					onAssignProjects={() => handleAssignProjects(record.id)}
				/>
			),
		},
	];

	if (!view) {
		return <AccessDenied />;
	}

	return (
		<>
			<Helmet>
				<title>Companies</title>
				<meta name="description" content="Companies Management" />
			</Helmet>
			<BreadCrumb
				add={create ? trans("Add new company") : null}
				onClick={() => setCompanyForm(true)}
				secButton={trans("Refresh")}
				secClick={() => setRefresh(true)}
				secLoading={isFetching}
			/>
			<CompaniesHead setFilter={setFilter} />
			<div className="bg-white-rounded-top mt-3 table-parent">
				<AntdTable
					columns={columns}
					loading={isFetching}
					dataSource={CompaniesData?.data || []}
					pageSize={limit}
					totalItems={CompaniesData?.pagination?.total || 0}
					currentPage={currentPage}
					onPageChange={(page) => setCurrentPage(page)}
					onLimitChange={(newLimit) => {
						setLimit(newLimit);
						setCurrentPage(1);
					}}
					onTableChange={handleTableChange}
					scrollX={true}
				/>
			</div>

			<FormCompany
				companyForm={companyForm}
				setCompanyForm={setCompanyForm}
				isEditing={typeof companyForm === "number"}
			/>
			<AssignProjectsPopup />
		</>
	);
};

export default Companies;
